import React from 'react';

import Avatar from '@atlaskit/avatar/avatar';
import type { SizeType } from '@atlaskit/avatar/types';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';

import { type PersonProfileData } from '../../types';

const styles = cssMap({
	fill: {
		width: '100%',
	},
});

interface ProfileIdentityProps {
	profileData: PersonProfileData;
	avatarSize?: SizeType;
	showDepartment?: boolean;
	showLocation?: boolean;
	showPronouns?: boolean;
	showLocalTime?: boolean;
	actions?: React.ReactNode;
	variant?: 'full' | 'compact';
}

export function ProfileIdentity({
	profileData,
	avatarSize = 'xlarge',
	showDepartment = false,
	showLocation = true,
	showPronouns = false,
	showLocalTime = false,
	actions,
	variant = 'full',
}: ProfileIdentityProps): React.JSX.Element {
	const { avatarUrl, name, pronouns, jobTitle, department, location, localTime } = profileData;

	const subtitle = showDepartment && department ? `${jobTitle} \u2022 ${department}` : jobTitle;

	const locationLine =
		showLocalTime && localTime && location ? `${location} \u2013 ${localTime}` : location;

	if (variant === 'compact') {
		return (
			<Stack xcss={styles.fill} space="space.100">
				<Inline spread="space-between">
					<Avatar src={avatarUrl ?? undefined} name={name} size={avatarSize} />
					{actions}
				</Inline>
				<Stack space="space.025">
					<Inline space="space.100" alignBlock="end">
						<Heading as="h2" size="medium">
							{name}
						</Heading>
						{showPronouns && pronouns && (
							<Text size="small" color="color.text.subtlest">
								{pronouns}
							</Text>
						)}
					</Inline>
					<Stack space="space.075">
						{subtitle && (
							<Text maxLines={1} color="color.text.subtlest">
								{subtitle}
							</Text>
						)}
						{showLocation && locationLine && (
							<Text maxLines={1} size="small" color="color.text.subtlest">
								{locationLine}
							</Text>
						)}
					</Stack>
				</Stack>
			</Stack>
		);
	}

	return (
		<Inline space="space.200" xcss={styles.fill} spread="space-between">
			<Inline space="space.200" alignBlock="center">
				<Avatar src={avatarUrl ?? undefined} name={name} size={avatarSize} />
				<Stack xcss={styles.fill} alignBlock="start">
					<Inline space="space.100" alignBlock="baseline">
						<Heading as="h2" size="medium">
							{name}
						</Heading>
						{showPronouns && pronouns && (
							<Text size="small" color="color.text.subtlest">
								{pronouns}
							</Text>
						)}
					</Inline>
					<Stack space="space.050">
						{subtitle && (
							<Text maxLines={1} color="color.text.subtlest">
								{subtitle}
							</Text>
						)}
						{showLocation && locationLine && (
							<Text maxLines={1} size="small" color="color.text.subtlest">
								{locationLine}
							</Text>
						)}
					</Stack>
				</Stack>
			</Inline>
			{actions}
		</Inline>
	);
}
